// @ts-check

/**
 * @file Lists the signed-in user's auth tokens with create, rename, and revoke actions.
 */

import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../src/components/button"
import Modal from "../../src/components/modal"
import ScreenTitle from "../../src/components/screen-title"
import Text from "../../src/components/text"
import TokenRow from "../../src/screens/auth-tokens/token-row"
import {UserAuthToken} from "../../src/frontend-models/user-auth-token"
import errorMessage from "../../src/shared/error-message"
import {useScreenLoading} from "../../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} AuthTokensListScreenProps */

/**
 * @typedef {object} AuthTokensListScreenState
 * @property {string | null} confirmingDeleteId Auth token id awaiting revoke confirmation.
 * @property {boolean} loading Whether the tokens are loading.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 * @property {InstanceType<typeof UserAuthToken>[]} tokens Loaded auth tokens.
 */

/**
 * Auth tokens list screen reachable from the profile.
 * @augments {ShapeComponent<AuthTokensListScreenProps, AuthTokensListScreenState>}
 */
class AuthTokensListScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params

  /** @type {ReturnType<typeof useRouter>} */
  router

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {AuthTokensListScreenState} */
  state = {
    confirmingDeleteId: null,
    loading: true,
    status: null,
    tokens: []
  }

  loadRequestId = 0

  /** Initializes route params and token loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    this.params = params
    this.router = useRouter()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadTokens()
    }, [params.created, params.saved])
  }

  /**
   * Renders the auth tokens list.
   * @returns {ReactNode} Auth tokens screen content.
   */
  render() {
    const {confirmingDeleteId, loading, status, tokens} = this.s
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="authTokensScreen"
        />
      )
    }

    return (
      <View
        style={styles.screen ||= {flex: 1}}
        testID="authTokensScreen"
      >
        <ScreenTitle>{_("Auth tokens")}</ScreenTitle>
        <Text
          style={styles[`intro-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 14,
            marginBottom: 16,
            maxWidth: 560
          }}
          testID="authTokensScreen/intro"
        >
          {_("Auth tokens let an AI bot or app act on your behalf through the API. Treat them like passwords — anyone with a token has your access until you revoke it.")}
        </Text>
        {status &&
          <Text
            style={styles[`status-${themeName}-${status.kind}`] ||= {
              color: status.kind === "success" ? colors.successText : colors.dangerText,
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 16
            }}
            testID="authTokensScreen/status"
          >
            {status.text}
          </Text>
        }
        <View
          style={styles.newButtonRow ||= {
            alignSelf: "flex-start",
            marginBottom: 16
          }}
          testID="authTokensScreen/newButtonRow"
        >
          <Button
            href="/auth-tokens/new"
            iconName="plus"
            label={_("Generate new token")}
            testID="authTokensScreen/newButton"
            variant="neutral"
          />
        </View>
        {tokens.length === 0 &&
          <Text
            style={styles[`empty-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 15
            }}
            testID="authTokensScreen/emptyText"
          >
            {_("You have no auth tokens yet")}
          </Text>
        }
        {tokens.map((token) => (
          <TokenRow
            createdAt={token.createdAt()}
            editPath={`/auth-tokens/${token.id()}/edit`}
            id={String(token.id())}
            key={String(token.id())}
            lastUsedAt={token.lastUsedAt()}
            name={token.name()}
            onDeletePress={this.tt.onDeletePress}
            tokenPrefix={token.tokenPrefix()}
          />
        ))}
        <Modal
          cardStyle={styles[`confirmModalCard-${themeName}`] ||= {
            borderColor: colors.danger,
            maxWidth: 420
          }}
          isVisible={Boolean(confirmingDeleteId)}
          onRequestClose={this.tt.onCancelDeletePress}
          testID="authTokensScreen/deleteConfirm"
        >
          <Text
            style={styles[`confirmTitle-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 16,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="authTokensScreen/deleteConfirm/title"
          >
            {_("Revoke this auth token? Apps using it will stop working immediately.")}
          </Text>
          <View
            style={styles.confirmActions ||= {
              flexDirection: "row",
              gap: 8
            }}
            testID="authTokensScreen/deleteConfirm/actions"
          >
            <Button
              iconName="xmark"
              label={_("Cancel")}
              onPress={this.tt.onCancelDeletePress}
              testID="authTokensScreen/deleteConfirm/cancelButton"
              variant="outline"
            />
            <Button
              iconName="trash"
              label={_("Revoke")}
              onPress={this.tt.onConfirmDeletePress}
              testID="authTokensScreen/deleteConfirm/confirmButton"
              variant="destructive"
            />
          </View>
        </Modal>
      </View>
    )
  }

  /**
   * Loads the signed-in user's auth tokens. The resource scopes the query to
   * the current user, so no client-side owner filter is needed.
   * @returns {Promise<void>} Resolves when token loading completes.
   */
  loadTokens = async () => {
    const requestId = ++this.loadRequestId
    try {
      const tokens = await UserAuthToken
        .query()
        .select(["createdAt", "id", "lastUsedAt", "name", "tokenPrefix"])
        .order([["createdAt", "desc"], ["id", "desc"]])
        .toArray()
      if (this.loadRequestId !== requestId) return
      /** @type {{kind: "error" | "success", text: string} | null} */
      let status = null
      if (this.tt.params.created === "1") {
        status = {kind: "success", text: _("The auth token was created")}
      } else if (this.tt.params.saved === "1") {
        status = {kind: "success", text: _("The auth token was renamed")}
      }
      this.s.tokens = tokens
      this.s.status = status
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load auth tokens")}: ${errorMessage(error)}`
      }
    }
  }

  /**
   * Opens revoke confirmation for an auth token.
   * @param {string} tokenId Auth token id to confirm.
   * @returns {void} Nothing.
   */
  onDeletePress = (tokenId) => {
    this.s.confirmingDeleteId = tokenId
    this.s.status = null
  }

  /**
   * Closes revoke confirmation.
   * @returns {void} Nothing.
   */
  onCancelDeletePress = () => {
    this.s.confirmingDeleteId = null
  }

  /**
   * Revokes the confirmed auth token.
   * @returns {Promise<void>} Resolves when revoke handling completes.
   * @rejects {Error} When no auth token id has been selected.
   */
  onConfirmDeletePress = async () => {
    const {confirmingDeleteId} = this.s
    if (!confirmingDeleteId) {
      throw new Error("Expected an auth token id before revoking")
    }
    const tokenToDelete = this.s.tokens.find((token) => String(token.id()) === confirmingDeleteId)
    if (!tokenToDelete) {
      throw new Error(`Auth token not found: ${confirmingDeleteId}`)
    }
    try {
      await tokenToDelete.destroy()
      this.s.tokens = this.s.tokens.filter((token) => String(token.id()) !== confirmingDeleteId)
      this.s.confirmingDeleteId = null
      this.s.status = {kind: "success", text: _("The auth token was revoked")}
    } catch (error) {
      this.s.confirmingDeleteId = null
      this.s.status = {kind: "error", text: errorMessage(error)}
    }
  }
}

/** @type {NamedExoticComponent<AuthTokensListScreenProps>} */
const AuthTokensListScreenComponent = memo(shapeComponent(AuthTokensListScreen))

export default AuthTokensListScreenComponent
